import { useId, useState, type FormEvent } from 'react'
import { LogIn } from 'lucide-react'
import { Link } from 'react-router-dom'

import { Button, Card, Field, TextInput } from '../components'
import { ApiRequestError } from '../lib/apiClient'
import { t } from '../lib/i18n'
import { routes } from '../lib/routes'

import { useSessionState, type Surface } from './session'

/**
 * The two doors.
 *
 * The Platform Console signs in at `POST /platform/auth/login`, which is checked
 * against `tenant_id IS NULL` only and takes NO business — a platform username
 * has no slug to give, and letting one be supplied is exactly how a tenant user
 * would try to authenticate here. The Owner Console signs in at
 * `POST /auth/login`, which takes the slug because usernames are unique per
 * tenant; that route REFUSES a platform account outright, and the message it
 * returns ("This account signs in through the platform console.") is shown as
 * it comes rather than being flattened into "wrong password".
 *
 * Both are cookie sessions, and both consoles share one `cb_session` cookie on
 * a browser — so signing in to one ends the other. That is a property of the
 * API, not a choice made here, and the form says so rather than surprising
 * someone mid-support-call.
 */
export function SignInScreen({ surface }: { surface: Surface }) {
  const { signInPlatform, signInOwner } = useSessionState()

  const slugId = useId()
  const userId = useId()
  const passwordId = useId()

  const [slug, setSlug] = useState('')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const isPlatform = surface === 'platform'

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      if (isPlatform) await signInPlatform(username.trim(), password)
      else await signInOwner(slug.trim(), username.trim(), password)
    } catch (cause) {
      // The server's own words. `INVALID_CREDENTIALS` never says which half was
      // wrong — that would enumerate accounts — and this must not improve on it.
      setError(cause instanceof ApiRequestError ? cause.message : t('state.errorTitle'))
      setPassword('')
    } finally {
      setBusy(false)
    }
  }

  const ready =
    username.trim().length > 0 && password.length > 0 && (isPlatform || slug.trim().length > 0)

  return (
    <div
      className="flex min-h-dvh items-center justify-center px-6 py-12"
      data-console={isPlatform ? 'platform' : 'owner'}
    >
      <Card surface="heavy" className="w-full max-w-[420px]">
        <h1 className="font-display text-xl font-extrabold tracking-[-0.4px] text-dark">
          {isPlatform ? t('auth.platformTitle') : t('auth.ownerTitle')}
        </h1>
        <p className="mt-1 text-[13.5px] leading-relaxed text-sub">
          {isPlatform ? t('auth.platformSub') : t('auth.ownerSub')}
        </p>

        <form className="mt-5" onSubmit={submit}>
          {isPlatform ? null : (
            <Field
              required
              label={t('auth.businessSlug')}
              hint={t('auth.businessSlugHint')}
              htmlFor={slugId}
            >
              <TextInput id={slugId} value={slug} onChange={setSlug} className="max-w-none" />
            </Field>
          )}

          <Field required label={t('auth.username')} htmlFor={userId}>
            <TextInput id={userId} value={username} onChange={setUsername} className="max-w-none" />
          </Field>

          <Field required label={t('auth.password')} htmlFor={passwordId}>
            <TextInput
              id={passwordId}
              type="password"
              value={password}
              onChange={setPassword}
              className="max-w-none"
            />
          </Field>

          {error ? (
            <p
              role="alert"
              className="mt-1 rounded-r border border-danger/30 bg-danger/10 p-3 text-[12.5px] leading-relaxed text-danger"
            >
              {error}
            </p>
          ) : null}

          <Button
            type="submit"
            variant="primary"
            className="mt-4 w-full"
            icon={<LogIn className="size-4" />}
            disabled={!ready || busy}
          >
            {busy ? t('auth.signingIn') : t('auth.signIn')}
          </Button>
        </form>

        <p className="mt-4 border-t border-muted/15 pt-3.5 text-xs leading-relaxed text-muted">
          {t('auth.oneSessionNote')}
        </p>
        <Link
          to={isPlatform ? routes.owner.overview() : routes.platform.overview()}
          className="mt-2 inline-block font-display text-xs font-bold text-accent-dark"
        >
          {isPlatform ? t('auth.toOwner') : t('auth.toPlatform')}
        </Link>
      </Card>
    </div>
  )
}
